import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { LifeBuoy, ChevronRight, Search } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { categoryLabel } from "@/pages/Support";

const STATUS_FILTERS = [
  { k: "all",         label: "Todos" },
  { k: "open",        label: "Abiertos" },
  { k: "in_progress", label: "En atención" },
  { k: "waiting_user", label: "Esperando usuario" },
  { k: "resolved",    label: "Resueltos" },
  { k: "closed",      label: "Cerrados" },
];

const STATUS_META = {
  open:          { label: "Abierto",         cls: "bg-blue-500/15 text-blue-600 dark:text-blue-300" },
  in_progress:   { label: "En atención",     cls: "bg-amber-500/15 text-amber-700 dark:text-amber-300" },
  waiting_user:  { label: "Esperando usuario", cls: "bg-purple-500/15 text-purple-600 dark:text-purple-300" },
  resolved:      { label: "Resuelto",        cls: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300" },
  closed:        { label: "Cerrado",         cls: "bg-slate-500/15 text-slate-600 dark:text-slate-300" },
};

export default function AdminSupport() {
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-support-tickets", status],
    queryFn: async () => (await api.get("/admin/support/tickets", { params: { status } })).data,
    refetchInterval: 30000,
  });

  const tickets = (data?.tickets || []).filter((t) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (
      t.subject.toLowerCase().includes(s) ||
      t.user_email.toLowerCase().includes(s) ||
      String(t.ticket_number).includes(s) ||
      (t.last_message_preview || "").toLowerCase().includes(s)
    );
  });

  const counts = data?.counts || {};

  return (
    <div data-testid="admin-support-page">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Soporte</p>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Tickets de usuarios</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Bandeja unificada de soporte.
            {counts.unread > 0 && (
              <span className="ml-2 inline-flex items-center gap-1 text-rose-600 font-medium">
                <span className="h-1.5 w-1.5 rounded-full bg-rose-500 animate-pulse" />
                {counts.unread} sin leer
              </span>
            )}
          </p>
        </div>
        <div className="relative w-64 max-w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar ticket, email, #número…"
            className="pl-9 h-9 bg-white/60 dark:bg-slate-900/40"
            data-testid="admin-support-search"
          />
        </div>
      </div>

      <div className="glass-panel rounded-2xl p-1 inline-flex flex-wrap gap-1 mb-4" data-testid="admin-support-filters">
        {STATUS_FILTERS.map((f) => {
          const n = f.k === "all"
            ? Object.values(counts).filter((v, i) => Object.keys(counts)[i] !== "unread").reduce((a, b) => a + b, 0)
            : counts[f.k] || 0;
          return (
            <button
              key={f.k}
              onClick={() => setStatus(f.k)}
              data-testid={`admin-support-filter-${f.k}`}
              className={cn(
                "px-3 h-9 rounded-xl text-xs font-medium inline-flex items-center gap-1.5 transition",
                status === f.k
                  ? "bg-red-600 text-white"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {f.label}
              <span className={cn(
                "text-[10px] px-1.5 py-0.5 rounded-full",
                status === f.k ? "bg-white/25" : "bg-white/60 dark:bg-slate-900/40"
              )}>{n}</span>
            </button>
          );
        })}
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 rounded-xl" />)}
        </div>
      ) : tickets.length === 0 ? (
        <div className="glass-panel rounded-2xl p-10 text-center">
          <LifeBuoy className="h-8 w-8 text-blue-500 mx-auto mb-2" />
          <p className="font-display text-lg">No hay tickets en esta bandeja</p>
          <p className="text-sm text-muted-foreground mt-1">Cuando un usuario abra un ticket lo verás aquí.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="admin-support-list">
          {tickets.map((t) => {
            const meta = STATUS_META[t.status] || STATUS_META.open;
            const ref = "#" + String(t.ticket_number).padStart(4, "0");
            return (
              <Link
                key={t.ticket_id}
                to={`/admin/support/${t.ticket_id}`}
                data-testid={`admin-ticket-${t.ticket_id}`}
                className={cn(
                  "block glass-card rounded-xl p-4 hover:-translate-y-0.5 hover:shadow-md transition",
                  t.unread_by_admin && "ring-2 ring-rose-500/30"
                )}
              >
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-xl bg-blue-500/10 grid place-items-center flex-none text-sm font-semibold text-blue-600 dark:text-blue-300">
                    {(t.user_name?.[0] || "?").toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-mono text-muted-foreground">{ref}</span>
                      <span className={cn("text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full", meta.cls)}>
                        {meta.label}
                      </span>
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        {categoryLabel(t.category)}
                      </span>
                      {t.unread_by_admin && (
                        <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-rose-500 text-white">
                          Nuevo
                        </span>
                      )}
                    </div>
                    <p className="font-medium truncate mt-0.5">{t.subject}</p>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                      {t.user_name} · {t.user_email}
                    </p>
                    <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                      {t.last_message_role === "admin" ? "Tú: " : ""}{t.last_message_preview}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      {formatDistanceToNow(new Date(t.last_message_at), { addSuffix: true, locale: es })}
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground flex-none" />
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
